import React, { useEffect, useState, useMemo, useContext } from "react";
import { UserContext } from "../store/UserContext";
import placeholder from "../images/user_placeholder.png";
import axios from "axios";


const EditProfile = () => {
    const { setUserImage } = useContext(UserContext);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        profile_img: null,
        password: "",
        password_confirmation: "",
    });
    const [previewImage, setPreviewImage] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalData, setOriginalData] = useState({});

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const user = res.data.user;
                setFormData({
                    name: user.name,
                    email: user.email,
                    profile_img: null,
                    password: "",
                    password_confirmation: "",
                });
                setOriginalData({
                    name: user.name,
                    email: user.email,
                    profile_img: user.profile_img || null,
                });
                const imageUrl = user.profile_img
                    ? `${process.env.REACT_APP_API_IMAGE_URL}/${user.profile_img}`
                    : placeholder;
                setPreviewImage(imageUrl);
                setUserImage(imageUrl);
            } catch (err) {
                console.error("Failed to fetch profile", err);
            }
        };
        fetchProfile();
    }, [setUserImage]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "profile_img") {
            const file = files[0];
            setFormData((prev) => ({ ...prev, profile_img: file }));
            setPreviewImage(file ? URL.createObjectURL(file) : null);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
            if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const isFormChanged = useMemo(() => {
        return (
            formData.name !== originalData.name ||
            formData.profile_img !== null ||
            formData.password.length > 0
        );
    }, [formData, originalData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormChanged) return; // no changes, do nothing

        setIsSubmitting(true);
        setErrors({});

        if (formData.password && formData.password !== formData.password_confirmation) {
            setErrors({ password_confirmation: "Password confirmation does not match" });
            setIsSubmitting(false);
            return;
        }

        const data = new FormData();
        data.append("name", formData.name);
        if (formData.profile_img) data.append("profile_img", formData.profile_img);
        if (formData.password) data.append("password", formData.password);
        if (formData.password_confirmation)
            data.append("password_confirmation", formData.password_confirmation);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/user/profile/update`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );


            const changedFields = [];
            if (formData.name !== originalData.name) changedFields.push("Name");
            if (formData.password.length > 0) changedFields.push("Password");
            if (formData.profile_img !== null) changedFields.push("Profile Image");

            // Reset passwords
            setFormData((prev) => ({ ...prev, password: "", password_confirmation: "" }));

            // Update original data
            setOriginalData({
                name: res.data.user.name,
                email: res.data.user.email,
                profile_img: res.data.user.profile_img || null,
            });

            // Update preview image
            if (res.data.user.profile_img) {
                const imagePath = res.data.user.profile_img.replace(/^public\//, "");
                const imageUrl = `${process.env.REACT_APP_API_IMAGE_URL}/${imagePath}`;
                setPreviewImage(imageUrl);
                setUserImage(imageUrl);
            } else {
                setPreviewImage(placeholder);
                setUserImage(placeholder);
            }



        } catch (err) {
            const apiErrors = err?.response?.data?.errors || {};
            setErrors(apiErrors);
            console.error("Failed to update profile", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="min-h-screen flex flex-col items-center justify-start bg-gray-50 font-poppins py-6 px-4">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">Edit Profile</h2>

            <form
                onSubmit={handleSubmit}
                className="w-full max-w-4xl flex flex-col md:flex-row gap-6"
            >
                {/* Left: Rectangular Profile Image */}
                <div className="flex flex-col items-center md:w-1/3">
                    <div className="relative w-48 h-56 sm:w-52 sm:h-64 md:w-60 md:h-72 mb-4 border border-gray-300 shadow-md overflow-hidden rounded-xl">
                        <img
                            src={previewImage || placeholder}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                        <label className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-2 rounded-full cursor-pointer font-medium shadow-lg hover:from-indigo-500 hover:to-blue-500 transition-all">
                            Upload
                            <input
                                type="file"
                                name="profile_img"
                                accept="image/*"
                                onChange={handleChange}
                                className="hidden"
                            />
                        </label>
                    </div>
                    {errors.profile_img && <p className="text-red-500 text-sm mt-1">{errors.profile_img}</p>}
                </div>

                {/* Right: Styled Form Fields */}
                <div className="md:w-2/3 bg-white p-6 rounded-2xl shadow-lg flex flex-col gap-5">
                    <div className="relative">
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-4 pt-6 pb-2 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 peer ${errors.name ? "border-red-500" : ""
                                }`}
                            placeholder=" "
                        />
                        <label className="absolute left-4 top-2 text-gray-500 text-sm transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-gray-700 peer-focus:text-sm">
                            Name
                        </label>
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>

                    <div className="relative">
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            readOnly
                            className="w-full px-4 pt-6 pb-2 border rounded-xl border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none peer"
                            placeholder=" "
                        />
                        <label className="absolute left-4 top-2 text-gray-500 text-sm">Email</label>
                    </div>

                    <div className="relative">
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder=" "
                            className={`w-full px-4 pt-6 pb-2 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 peer ${errors.password ? "border-red-500" : ""
                                }`}
                        />
                        <label className="absolute left-4 top-2 text-gray-500 text-sm peer-focus:text-gray-700 peer-focus:text-sm">
                            New Password
                        </label>
                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>

                    <div className="relative">
                        <input
                            type="password"
                            name="password_confirmation"
                            value={formData.password_confirmation}
                            onChange={handleChange}
                            placeholder=" "
                            className={`w-full px-4 pt-6 pb-2 border rounded-xl border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 peer ${errors.password_confirmation ? "border-red-500" : ""
                                }`}
                        />
                        <label className="absolute left-4 top-2 text-gray-500 text-sm peer-focus:text-gray-700 peer-focus:text-sm">
                            Confirm New Password
                        </label>
                        {errors.password_confirmation && (
                            <p className="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting || !isFormChanged}
                        className={`mt-4 w-full py-3 rounded-xl text-white font-semibold transition ${isSubmitting || !isFormChanged
                            ? "bg-blue-400/50 cursor-not-allowed"
                            : "bg-blue-500 hover:bg-blue-600"
                            }`}
                    >
                        {isSubmitting ? "Updating..." : "Update Profile"}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default EditProfile;
